import React, { useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ShieldX, ChevronDown, ChevronUp, Sparkles, Loader2, RefreshCw } from "lucide-react";
import { CreateSupplierAction, AdoptIbanAction, MergeRelatedAction, AssignCardAction, MoveCompanyAction, ConfirmMergeAction, ReverseChargeAction } from "@/components/InboxCheckActions";

const STYLE = {
  green: { cls: "border-emerald-300 bg-emerald-50 text-emerald-900", Icon: ShieldCheck, label: "Alle Prüfungen bestanden" },
  amber: { cls: "border-amber-300 bg-amber-50 text-amber-900", Icon: ShieldAlert, label: "Prüfen vor Buchung" },
  red: { cls: "border-rose-300 bg-rose-50 text-rose-900", Icon: ShieldX, label: "Buchung gestoppt" },
};
const DOT = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-rose-500" };

/** Ampel + 8-Punkte-Prüfprotokoll eines Posteingang-Belegs, mit KI-Erklärung auf Abruf. */
export function InboxChecksPanel({ companyId, item, onUpdated, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [explaining, setExplaining] = useState(false);
  const [explanation, setExplanation] = useState(item.checks?.explanation || "");
  const checks = item.checks;
  if (!checks) return null;
  const st = STYLE[checks.status] || STYLE.amber;
  const issues = checks.checks.filter((c) => c.status !== "green").length;

  const rerun = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${item.id}/checks`);
      const { routing, ...checks } = r.data;
      onUpdated?.({ ...item, checks, routing: routing || item.routing });
      toast.success("Prüfprotokoll aktualisiert");
    } catch (e) { toast.error(e.response?.data?.detail || "Prüfung fehlgeschlagen"); } finally { setBusy(false); }
  };
  const explain = async () => {
    setExplaining(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/inbox/${item.id}/checks/explain`);
      setExplanation(r.data.explanation);
    } catch (e) { toast.error(e.response?.data?.detail || "KI-Erklärung nicht verfügbar"); } finally { setExplaining(false); }
  };

  return (
    <div className={`mt-2 rounded-md border px-2.5 py-1.5 text-[11px] ${st.cls}`} data-testid={`inbox-checks-${item.id}`} data-status={checks.status}>
      <button type="button" className="w-full flex items-center justify-between gap-2" onClick={() => setOpen(!open)} data-testid={`inbox-checks-toggle-${item.id}`}>
        <span className="flex items-center gap-1.5 font-medium">
          <st.Icon className="h-3.5 w-3.5" /> {st.label}
          <span className="font-normal opacity-80">· {checks.checks.length - issues}/{checks.checks.length} grün{!open && issues > 0 ? ` · ${issues} Hinweis${issues === 1 ? "" : "e"}` : ""}</span>
          {item.autobook_blocked_by_checks && <span className="ml-1 rounded bg-white/70 px-1 py-px text-[10px]">Auto-Buchung gestoppt</span>}
        </span>
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <div className="mt-1.5 space-y-1">
          {checks.checks.map((c) => (
            <div key={c.id} className="flex flex-wrap items-start gap-1.5" data-testid={`inbox-check-${item.id}-${c.id}`}>
              <span className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${DOT[c.status]}`} />
              <span><span className="font-medium">{c.label}:</span> {c.detail}</span>
              {(c.action === "create_supplier" || (c.id === "supplier" && item.extracted?.supplier_name)) && c.status !== "green" && <CreateSupplierAction companyId={companyId} item={item} onUpdated={onUpdated} />}
              {c.action === "adopt_iban" && c.status !== "green" && <AdoptIbanAction companyId={companyId} item={item} onUpdated={onUpdated} />}
              {c.action === "merge_related" && <MergeRelatedAction companyId={companyId} item={item} onRefresh={onRefresh} />}
              {c.action === "assign_card" && <AssignCardAction companyId={companyId} item={item} onUpdated={onUpdated} onRefresh={onRefresh} />}
              {c.action === "move_company" && <MoveCompanyAction companyId={companyId} item={item} onRefresh={onRefresh} />}
              {c.action === "reverse_charge" && <ReverseChargeAction companyId={companyId} item={item} onUpdated={onUpdated} />}
              {c.action === "confirm_merge" && <ConfirmMergeAction companyId={companyId} item={item} onUpdated={onUpdated} onRefresh={onRefresh} />}
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {issues > 0 && (
              <button type="button" onClick={explain} disabled={explaining} className="inline-flex items-center gap-1 rounded border border-current/30 bg-white/60 px-2 py-0.5 hover:bg-white" data-testid={`inbox-checks-explain-${item.id}`}>
                {explaining ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} KI erklärt, was zu tun ist
              </button>
            )}
            <button type="button" onClick={rerun} disabled={busy} className="inline-flex items-center gap-1 rounded border border-current/30 bg-white/60 px-2 py-0.5 hover:bg-white" data-testid={`inbox-checks-rerun-${item.id}`}>
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Neu prüfen
            </button>
          </div>
          {explanation && <div className="mt-1 whitespace-pre-wrap rounded bg-white/70 p-2 text-slate-800" data-testid={`inbox-checks-explanation-${item.id}`}>{explanation}</div>}
        </div>
      )}
    </div>
  );
}
